import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react'
import {LayoutSplashScreen} from '@/_metronic/layout/core'
import {AuthModel, GenerateTokenRequest, ProfileModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {doLogout, generateToken, getIamUserAccess, getUserByToken} from './_requests'
import {findSingleMenu, groupMenusByLevelAndOrder, WithChildren} from '@/_metronic/helpers'
import {useLocation, useNavigate} from 'react-router-dom'
import {uniqBy} from 'lodash'
import {MenuAccess} from '../../user-management/user-management-edit/core/_model'
import {menus} from '@/_metronic/layout/core/_menu'

type AuthContextProps = {
  auth: AuthModel | undefined
  token: string | null
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: ProfileModel | undefined
  setCurrentUser: Dispatch<SetStateAction<ProfileModel | undefined>>
  menuAccess: Array<MenuAccess>
  saveMenuAccess: (accesses: Array<MenuAccess>) => void
  logout: () => void
  hasAccess: any
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
}

const initAuthContextPropsState: AuthContextProps = {
  auth: authHelper.getAuth(),
  token: authHelper.getToken(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  menuAccess: [],
  saveMenuAccess: () => {},
  hasAccess: null,
  loading: true,
  setLoading: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [token, setToken] = useState<string | null>(authHelper.getToken())
  const [currentUser, setCurrentUser] = useState<ProfileModel | undefined>()
  const [menuAccess, setMenuAccess] = useState<Array<MenuAccess>>([])
  const [loading, setLoading] = useState<boolean>(true)
  // const navigate = useNavigate()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    doLogout().catch((err) => console.error(err))
    saveAuth(undefined)
    setCurrentUser(undefined)
    setMenuAccess([])
    // navigate('/auth')
  }

  useEffect(() => {
    if (!token) {
      // generate token
      getToken()
    }
  }, [token])

  const getToken = async () => {
    const accessToken = await authHelper.fetchToken()
    setToken(accessToken)
  }

  const parent = findSingleMenu(menuAccess, window.location.pathname)

  const hasAccess = useMemo(() => {
    if (parent?.length) {
      const menu = parent?.[0]
      return menu?.has_access
    }
  }, [parent])

  return (
    <AuthContext.Provider
      value={{
    auth,
    token,
    saveAuth,
    currentUser,
    setCurrentUser,
    logout,
    menuAccess,
    saveMenuAccess: setMenuAccess,
    hasAccess,
    loading,
    setLoading,
  }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {token, logout, setCurrentUser, saveMenuAccess, setLoading} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const location = useLocation()

  const notForbiddenRoutes = useMemo(() => {
    return (
      !location?.pathname?.startsWith('/order') &&
      !location?.pathname?.startsWith('/rating/') &&
      !location?.pathname?.startsWith('/privacy-policy') &&
      !location?.pathname?.startsWith('/forgot-password/')
    )
  }, [location])

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
  const requestIam = async () => {
    try {
      const {data} = await getIamUserAccess()
      const menuAccess = groupMenusByLevelAndOrder(data?.response?.data?.menu_access)
      saveMenuAccess(menuAccess)
    } catch (error) {
      console.error(error)
    }
  }

  const requestUser = async (apiToken: string) => {
    try {
      setLoading(true)
      if (!didRequest.current) {
        const {data} = await getUserByToken(apiToken)
        if (data) {
          setCurrentUser(data?.response?.data)
          if (notForbiddenRoutes && location.pathname !== '/error/404') {
            await requestIam()
          }
        }
      }
    } catch (error) {
      console.error(error)
      if (!didRequest.current) {
        logout()
      }
    } finally {
      setShowSplashScreen(false)
      setLoading(false)
    }
    return () => (didRequest.current = true)
  }

  if (notForbiddenRoutes) {
    if (token) {
      requestUser(token)
    } else {
      logout()
      setShowSplashScreen(false)
      setLoading(false)
    }
  } else {
    setShowSplashScreen(false)
    setLoading(false)
  }
}, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
